import { ColumnMinGap } from 'components/AccountDetails'
import { useVaultAprHistory } from 'hooks/berachain/useVaultAprHistory'
import React, { useEffect, useState } from 'react'
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { useTheme } from 'styled-components'
import { ThemedText } from 'theme'
import { formatNumber } from 'utils/formatCurrencyAmount'

import * as S from './styleds'
import { filterDataByTimeframe, selectXAxisTimeFormat, SnapshotType } from './utils/chartHelpers'

type AprViewType = {
  timeframe: '7' | '30' | '90' | 'max'
  snapshots: SnapshotType[]
  rewardVault?: string
  rewardVaultRewardSymbol?: string
}

type CustomTickLabelProps = {
  x?: number
  y?: number
  payload?: any
  axisType?: 'xAxis' | 'yAxis'
  theme: any
}

type CustomTooltipProps = {
  active?: boolean
  payload?: any[]
  theme?: any
  tooltipDateFormatter: (timestamp: number | string) => string
  rewardVaultRewardSymbol?: string
}

type AprChartPoint = {
  timestamp: number
  poolApr: number
  rewardVaultApr: number
  totalApr: number
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({
  active,
  payload,
  theme,
  tooltipDateFormatter,
  rewardVaultRewardSymbol,
}) => {
  if (active && payload && payload.length) {
    const tooltipTimestamp = payload[0]?.payload?.timestamp ?? payload[0]?.payload?.t

    const poolApr = Number(payload[0]?.payload?.poolApr ?? 0)
    const rewardVaultApr = Number(payload[0]?.payload?.rewardVaultApr ?? 0)
    const totalApr = Number(payload[0]?.payload?.totalApr ?? poolApr + rewardVaultApr)

    return (
      <S.StrategyTooltipFlex>
        <ThemedText.DeprecatedBody fontSize="14px" fontWeight="700" lineHeight="20px" color={theme.textPrimary}>
          {tooltipDateFormatter(tooltipTimestamp)}
        </ThemedText.DeprecatedBody>
        <ColumnMinGap>
          <S.StrategyTooltipRow>
            <ThemedText.DeprecatedBody fontSize="12px" fontWeight="400" lineHeight="16px" color={theme.textSecondary}>
              Total APR:
            </ThemedText.DeprecatedBody>
            <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="16px" color={theme.textPrimary}>
              {formatNumber(totalApr)}%
            </ThemedText.DeprecatedBody>
          </S.StrategyTooltipRow>
          <S.StrategyTooltipRow>
            <ThemedText.DeprecatedBody fontSize="12px" fontWeight="400" lineHeight="16px" color={theme.textSecondary}>
              {rewardVaultRewardSymbol || 'Reward'} APR:
            </ThemedText.DeprecatedBody>
            <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="16px" color={theme.accentAction}>
              {formatNumber(rewardVaultApr)}%
            </ThemedText.DeprecatedBody>
          </S.StrategyTooltipRow>
          <S.StrategyTooltipRow>
            <ThemedText.DeprecatedBody fontSize="12px" fontWeight="400" lineHeight="16px" color={theme.textSecondary}>
              Pool APR:
            </ThemedText.DeprecatedBody>
            <ThemedText.DeprecatedBody fontSize="12px" fontWeight="500" lineHeight="16px" color="rgb(148, 163, 184)">
              {formatNumber(poolApr)}%
            </ThemedText.DeprecatedBody>
          </S.StrategyTooltipRow>
        </ColumnMinGap>
      </S.StrategyTooltipFlex>
    )
  }

  return null
}

const AprView: React.FC<AprViewType> = ({ timeframe, snapshots, rewardVault, rewardVaultRewardSymbol }) => {
  const theme = useTheme()
  const [filteredData, setFilteredData] = useState<AprChartPoint[]>([])

  const xAxisFormatter = selectXAxisTimeFormat(timeframe)
  const tooltipDateFormatter = (timestamp: number | string) =>
    new Date(Number(timestamp) * 1000).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    })

  const { data: rewardVaultSnapshots } = useVaultAprHistory(
    {
      vaultId: rewardVault,
    },
    {
      skip: !rewardVault,
      refetchOnWindowFocus: false,
    }
  )

  useEffect(() => {
    const parsePoolAprData = () => {
      if (!snapshots?.length) {
        return [] as { timestamp: number; apr: number }[]
      }

      const sortedSnapshots = snapshots
        .map((snapshot) => ({
          ...snapshot,
          timestamp: Number(snapshot.timestamp),
        }))
        .sort((a, b) => a.timestamp - b.timestamp)

      return filterDataByTimeframe(sortedSnapshots, timeframe)
        .map((snapshot) => ({
          timestamp: Number(snapshot.timestamp),
          apr: parseFloat(snapshot.apr),
        }))
        .filter((snapshot) => Number.isFinite(snapshot.apr))
        .filter((_, index, arr) => index !== arr.length - 1)
    }

    const parseRewardVaultAprData = () => {
      if (!rewardVaultSnapshots?.length) {
        return [] as { timestamp: number; apr: number }[]
      }

      return filterDataByTimeframe(
        rewardVaultSnapshots
          .map((snapshot) => ({
            timestamp: Number(snapshot.timestamp),
            apr: Number.parseFloat(snapshot.apr) * 100,
          }))
          .filter((snapshot) => Number.isFinite(snapshot.apr))
          .sort((a, b) => a.timestamp - b.timestamp),
        timeframe
      ).filter((_, index, arr) => index !== arr.length - 1)
    }

    const poolData = parsePoolAprData()
    const rewardVaultData = parseRewardVaultAprData()

    if (!poolData.length && !rewardVaultData.length) {
      setFilteredData([])
      return
    }

    const combinedByDay = new Map<number, { timestamp: number; poolApr: number; rewardVaultApr: number }>()

    poolData.forEach((point) => {
      const dayTimestamp = Math.floor(point.timestamp / 86400) * 86400
      const existing = combinedByDay.get(dayTimestamp)

      combinedByDay.set(dayTimestamp, {
        timestamp: dayTimestamp,
        poolApr: point.apr,
        rewardVaultApr: existing?.rewardVaultApr ?? 0,
      })
    })

    rewardVaultData.forEach((point) => {
      const dayTimestamp = Math.floor(point.timestamp / 86400) * 86400
      const existing = combinedByDay.get(dayTimestamp)

      combinedByDay.set(dayTimestamp, {
        timestamp: dayTimestamp,
        poolApr: existing?.poolApr ?? 0,
        rewardVaultApr: point.apr,
      })
    })

    const combinedData = Array.from(combinedByDay.values())
      .sort((a, b) => a.timestamp - b.timestamp)
      .map((point) => ({
        ...point,
        totalApr: point.poolApr + point.rewardVaultApr,
      }))

    setFilteredData(combinedData)
  }, [timeframe, snapshots, rewardVaultSnapshots])

  const CustomTickLabel: React.FC<CustomTickLabelProps> = ({ x, y, payload, axisType, theme }) => {
    const value = axisType === 'xAxis' ? xAxisFormatter(payload.value) : `${payload.value}%`

    return (
      <text
        x={x}
        y={y}
        dy={axisType === 'xAxis' ? 10 : 0}
        dx={axisType === 'xAxis' ? 0 : -14}
        textAnchor="middle"
        fill={theme.textSecondary}
        fontSize={12}
      >
        {value}
      </text>
    )
  }

  return (
    <S.GraphFlex style={{ overflow: 'hidden' }}>
      <ResponsiveContainer width="100%" height={362} minWidth="100%">
        <AreaChart data={filteredData} margin={{ top: 24, right: 8, left: -8, bottom: 0 }}>
          <XAxis
            dataKey="timestamp"
            stroke="white"
            tickLine={false}
            axisLine={false}
            tick={<CustomTickLabel axisType="xAxis" theme={theme} />}
            minTickGap={5}
            tickFormatter={xAxisFormatter}
          />
          <YAxis
            dataKey="totalApr"
            stroke="white"
            tickLine={false}
            axisLine={false}
            tick={<CustomTickLabel axisType="yAxis" theme={theme} />}
            tickFormatter={(value) => `${value}%`}
            domain={[0, 'auto']}
            tickCount={8}
          />
          <Tooltip
            content={
              <CustomTooltip
                theme={theme}
                tooltipDateFormatter={tooltipDateFormatter}
                rewardVaultRewardSymbol={rewardVaultRewardSymbol}
              />
            }
          />
          <Area
            type="monotone"
            dataKey="poolApr"
            stackId="apr"
            stroke="rgb(148, 163, 184)"
            fill="rgb(148, 163, 184)"
            fillOpacity={0.08}
          />
          <Area
            type="monotone"
            dataKey="rewardVaultApr"
            stackId="apr"
            /* eslint-disable-next-line react/prop-types */
            stroke={theme.accentAction}
            /* eslint-disable-next-line react/prop-types */
            fill={theme.accentAction}
            fillOpacity={0.08}
          />
        </AreaChart>
      </ResponsiveContainer>
    </S.GraphFlex>
  )
}
export default AprView
